import React from "react";

/**
 * Unified header for platform-admin pages. Consistent eyebrow + title +
 * subtitle on the left, action buttons on the right. No back button by
 * design — admin pages are reached from the sidebar, so a back arrow only
 * adds noise. Pair with a `mx-auto max-w-[1500px]` page wrapper for the
 * standard admin width.
 */
export default function AdminPageHeader({
  eyebrow,
  title,
  subtitle,
  icon,
  bareIcon = false,
  actions,
  className = "",
}: {
  eyebrow?: string;
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** Render `icon` as-is, without the padded indigo box (e.g. an avatar/photo). */
  bareIcon?: boolean;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 flex-wrap mb-6 ${className}`}>
      <div className="flex items-center gap-4 min-w-0">
        {icon &&
          (bareIcon ? (
            <div className="shrink-0">{icon}</div>
          ) : (
            <div className="p-3 bg-indigo-50 rounded-2xl text-indigo-600 shrink-0">{icon}</div>
          ))}
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{eyebrow}</p>
          )}
          <h1 className="text-2xl font-bold text-slate-800 leading-6">{title}</h1>
          {subtitle && <div className="text-slate-500 text-sm mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

type AdminButtonVariant = "primary" | "secondary" | "danger";

const ADMIN_BTN: Record<AdminButtonVariant, string> = {
  primary: "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-500/20",
  secondary: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300",
  danger: "bg-rose-600 hover:bg-rose-700 text-white shadow-sm shadow-rose-500/20",
};

/**
 * Consistent admin action button: same shape (rounded-xl), size (px-4 py-2.5),
 * weight, and icon+label rhythm across every admin page.
 */
export function AdminButton({
  variant = "primary",
  icon,
  children,
  className = "",
  ...rest
}: { variant?: AdminButtonVariant; icon?: React.ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ADMIN_BTN[variant]} ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
